import { ref, markRaw, onMounted, onUnmounted, type Component, type ConcreteComponent } from 'vue';
import { useMobile } from '@composables/useMobile';
import type { ModalState, ModalStateProps } from '@app/interfaces/modalState.interface';
import type { UseMobileModalReturn } from '@app/interfaces/useMobileModalReturn.interface';

const modalState = ref<ModalState>({
	isOpen: false,
	component: null,
	props: {}
});


/**
 * Composable for managing mobile modal state.
 * Provides methods to open/close modals with dynamic components.
 *
 * @returns Modal state and control methods
 *
 * @example
 * const { open, close, modalState } = useMobileModal();
 * open(MyComponent, { title: 'Hello' });
 *
 * @note Props are stored as-is. For optimal memory usage, prefer passing
 * plain objects/primitives rather than large reactive objects (refs, reactive).
 * Always call close() when done to release references.
 */
export const useMobileModal = (): UseMobileModalReturn => {
	const open = (component: Component, props: ModalStateProps = {}): void => {
		modalState.value = {
			isOpen: true,
			component: markRaw(component) as ConcreteComponent,
			props
		};
	};

	const close = (): void => {
		modalState.value = {
			isOpen: false,
			component: null,
			props: {}
		};
	};

	return {
		modalState,
		open,
		close
	};
};

export const useModalBreakpoint = (): void => {
	let stopMobileWatch: (() => void) | undefined;
	const { onMobileChange } = useMobile();

	onMounted(() => {
		stopMobileWatch = onMobileChange((isMobileNow, wasMobile) => {
			if (!(wasMobile && !isMobileNow && modalState.value.isOpen)) return;

			modalState.value = {
				isOpen: false,
				component: null,
				props: {}
			};
		});
	});

	onUnmounted(() => {
		if (stopMobileWatch) {
			stopMobileWatch();
			stopMobileWatch = undefined;
		}
	});
};
